/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Universal Shared Types & Schemas for Admin User Directory & Profile (Task 11.4.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

/**
 * Account Role Schema
 */
export const AdminUserRoleSchema = z.enum(['super_admin', 'admin', 'user']);
export type AdminUserRole = z.infer<typeof AdminUserRoleSchema>;

/**
 * Account Lifecycle Status Schema
 */
export const AdminUserStatusSchema = z.enum(['active', 'suspended', 'banned', 'pending_verification']);
export type AdminUserStatus = z.infer<typeof AdminUserStatusSchema>;

/**
 * Single User row in backoffice user directory.
 */
export const AdminUserListItemSchema = z
  .object({
    id: z.string().openapi({ description: 'User account UUID', example: 'usr_123456789' }),
    email: z.string().email().openapi({ description: 'Primary email address', example: 'alex@example.com' }),
    fullName: z.string().openapi({ description: 'User full display name', example: 'Alex Morgan' }),
    avatarUrl: z.string().nullable().optional().openapi({ description: 'Avatar image URL' }),
    role: AdminUserRoleSchema.openapi({ description: 'Privilege level', example: 'user' }),
    status: AdminUserStatusSchema.openapi({ description: 'Account status', example: 'active' }),
    workspaceId: z.string().openapi({ description: 'Default workspace ID', example: 'ws_123456' }),
    workspaceName: z.string().openapi({ description: 'Workspace display name', example: 'Acme Studio' }),
    walletBalance: z.number().openapi({ description: 'Current AI credit balance', example: 12500 }),
    lifetimeCredits: z.number().openapi({ description: 'Total lifetime credits earned/purchased', example: 50000 }),
    activePlanName: z.string().nullable().optional().openapi({ description: 'Active subscription tier', example: 'Pro Monthly Plan' }),
    activePlanType: z.string().nullable().optional().openapi({ description: 'Plan type (subscription/token_pack)' }),
    totalOrdersCount: z.number().openapi({ description: 'Number of paid/completed orders', example: 3 }),
    lifetimeValueUsd: z.number().openapi({ description: 'Total revenue generated (LTV in USD)', example: 87.0 }),
    emailVerified: z.boolean().openapi({ description: 'Whether email has been verified' }),
    createdAt: z.string().openapi({ description: 'Account registration ISO timestamp' }),
    updatedAt: z.string().openapi({ description: 'Last update ISO timestamp' }),
  })
  .openapi('AdminUserListItem');

export type AdminUserListItem = z.infer<typeof AdminUserListItemSchema>;

/**
 * Query filters schema for listing users.
 */
export const AdminUserQuerySchema = z
  .object({
    role: z.enum(['all', 'super_admin', 'admin', 'user']).default('all').openapi({ description: 'Filter by account role' }),
    status: z.enum(['all', 'active', 'suspended', 'banned']).default('all').openapi({ description: 'Filter by status' }),
    search: z.string().optional().openapi({ description: 'Search by full name, email, or workspace name' }),
    page: z.coerce.number().int().min(1).default(1).openapi({ description: 'Page number' }),
    limit: z.coerce.number().int().min(1).max(100).default(20).openapi({ description: 'Rows per page' }),
  })
  .openapi('AdminUserQuery');

export type AdminUserQuery = z.infer<typeof AdminUserQuerySchema>;

/**
 * KPI Telemetry Summary for User Management.
 */
export const AdminUsersSummarySchema = z
  .object({
    totalUsers: z.number().openapi({ description: 'Total registered user accounts' }),
    activeUsers: z.number().openapi({ description: 'Total active accounts' }),
    suspendedUsers: z.number().openapi({ description: 'Total suspended or banned accounts' }),
    totalCreditsInCirculation: z.number().openapi({ description: 'Sum of all active wallet credit balances' }),
  })
  .openapi('AdminUsersSummary');

export type AdminUsersSummary = z.infer<typeof AdminUsersSummarySchema>;

/**
 * Order record summary for 360-degree user profile.
 */
export const AdminUserOrderBriefSchema = z.object({
  id: z.string().openapi({ description: 'Order ID' }),
  planName: z.string().openapi({ description: 'Plan name' }),
  amountPaid: z.number().openapi({ description: 'Amount paid' }),
  currency: z.string().openapi({ description: 'Currency' }),
  status: z.string().openapi({ description: 'Order status' }),
  gatewayCode: z.string().openapi({ description: 'Payment gateway' }),
  createdAt: z.string().openapi({ description: 'Order date' }),
});

export type AdminUserOrderBrief = z.infer<typeof AdminUserOrderBriefSchema>;

/**
 * Detailed 360-degree profile for single user view.
 */
export const AdminUserDetailSchema = z
  .object({
    user: AdminUserListItemSchema,
    recentOrders: z.array(AdminUserOrderBriefSchema).openapi({ description: 'Recent payment invoices' }),
    stats: z.object({
      totalTokensUsed: z.number().openapi({ description: 'Estimated tokens generated' }),
      totalApiKeys: z.number().openapi({ description: 'Active developer API keys' }),
      totalDocuments: z.number().openapi({ description: 'Created documents' }),
    }),
  })
  .openapi('AdminUserDetail');

export type AdminUserDetail = z.infer<typeof AdminUserDetailSchema>;

/**
 * Request schema for updating user status (banning/suspending/activating).
 */
export const AdminUpdateUserStatusSchema = z
  .object({
    status: z.enum(['active', 'suspended', 'banned']).openapi({ description: 'New account status' }),
    reason: z.string().optional().openapi({ description: 'Reason for status transition (for audit log)' }),
  })
  .openapi('AdminUpdateUserStatus');

export type AdminUpdateUserStatusInput = z.infer<typeof AdminUpdateUserStatusSchema>;

/**
 * Request schema for manual wallet credit adjustment (Task 11.4.2).
 */
export const AdminCreditAdjustmentSchema = z
  .object({
    amount: z
      .number()
      .refine((val) => val !== 0, { message: 'Adjustment amount cannot be zero.' })
      .openapi({ description: 'Adjustment amount: positive to add credits, negative to deduct' }),
    reason: z
      .string()
      .min(3, { message: 'Reason must be at least 3 characters.' })
      .openapi({ description: 'Mandatory audit rationale for adjusting wallet credits' }),
    notifyUser: z
      .boolean()
      .optional()
      .default(false)
      .openapi({ description: 'Whether to dispatch notification email to workspace owner' }),
  })
  .openapi('AdminCreditAdjustment');

export type AdminCreditAdjustmentInput = z.infer<typeof AdminCreditAdjustmentSchema>;

/**
 * Response schema for manual wallet credit adjustment.
 */
export const AdminCreditAdjustmentResponseSchema = z
  .object({
    workspaceId: z.string().openapi({ description: 'Target workspace ID' }),
    previousBalance: z.number().openapi({ description: 'Wallet balance before adjustment' }),
    adjustedAmount: z.number().openapi({ description: 'Amount added (+) or deducted (-)' }),
    newBalance: z.number().openapi({ description: 'Wallet balance after adjustment' }),
    ledgerEntryId: z.string().openapi({ description: 'ID of immutable ledger entry' }),
    adminId: z.string().openapi({ description: 'Admin user ID who executed adjustment' }),
    createdAt: z.string().openapi({ description: 'ISO 8601 timestamp of adjustment' }),
  })
  .openapi('AdminCreditAdjustmentResponse');

export type AdminCreditAdjustmentResponse = z.infer<typeof AdminCreditAdjustmentResponseSchema>;

/**
 * Response schema for administrative dynamic user impersonation (Task 11.4.3).
 */
export const AdminImpersonationResponseSchema = z
  .object({
    token: z.string().openapi({ description: 'Short-lived impersonation JWT access token (15 mins)' }),
    expiresIn: z.number().openapi({ description: 'Session duration in seconds (900s)' }),
    expiresAt: z.number().openapi({ description: 'Epoch timestamp (seconds) when session expires' }),
    targetUser: z.object({
      id: z.string().openapi({ description: 'Target user ID' }),
      email: z.string().openapi({ description: 'Target user email' }),
      fullName: z.string().openapi({ description: 'Target user full name' }),
      role: z.string().openapi({ description: 'Target user account role' }),
      workspaceId: z.string().openapi({ description: 'Primary workspace ID' }),
    }),
    redirectUrl: z.string().openapi({ description: 'Dynamic Zero-Hardcoded destination URL to User Workspace' }),
    adminReturnUrl: z.string().openapi({ description: 'Dynamic origin URL to return back to Admin Portal' }),
  })
  .openapi('AdminImpersonationResponse');

export type AdminImpersonationResponse = z.infer<typeof AdminImpersonationResponseSchema>;


